import React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/**
 * CO-356 — direction-aware Tabs root.
 *
 * 🔴 Radix's `Tabs.Root` does NOT inherit the page direction. When no `dir` prop is passed it
 * resolves through `DirectionProvider`, and with no provider mounted it falls back to the literal
 * string `'ltr'` and STAMPS `dir="ltr"` on the root element. Because `dir` is an inherited HTML
 * attribute, every descendant of a tab panel then computes `direction: ltr` even while
 * `<html dir="rtl">` — so an Arabic page silently renders LTR from the tab root down: logical
 * properties (`ms-`/`me-`/`ps-`/`pe-`/`start-`/`end-`), text alignment, flex/grid main-axis order
 * and float direction all resolve the wrong way inside every panel.
 *
 * ⚠️ Note what does NOT change, because it is easy to assume the opposite: Tailwind's `rtl:` variant
 * is `&:where([dir="rtl"], [dir="rtl"] *)`, which matches on ANY `[dir="rtl"]` ancestor — `<html>`
 * still qualifies. So `rtl:space-x-reverse` / `rtl:ml-2` / `rtl:rotate-180` were firing inside the
 * panels the whole time, on top of an LTR computed direction. The panels were not merely unmirrored,
 * they were incoherent: RTL-intended overrides layered over LTR layout. The corollary is that this
 * fix cannot switch on a set of previously-dormant `rtl:` utilities — they were never dormant — so
 * it changes computed direction only.
 *
 * Measured on dev @ c889fc73: on `/settings/core-hr/asset-management` in Arabic, the toggle card
 * inside the Replenishment panel sat at x=48 with the switch at x=65 (RTL would place it > 576),
 * and walking the ancestor chain found an explicit `dir="ltr"` DIV — Radix's root — directly under
 * a `rtl` page container. The pre-existing OTP card on the same page measured identically, which is
 * what identified the shared primitive rather than any one view's markup as the cause.
 *
 * The fix reads the active locale's direction from i18next and hands it to Radix, so panels inherit
 * the app locale. An explicit `dir` prop from a caller still wins (nothing passes one today). This
 * is a no-op in `en`/`fr` — `i18n.dir()` returns `'ltr'` there, which is exactly what Radix was
 * hardcoding — and it additionally restores correct Left/Right arrow-key ordering on the tab list
 * under RTL, which Radix keys off the same `dir`.
 */
const Tabs = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ dir, ...props }, ref) => {
  const { i18n } = useTranslation();
  return <TabsPrimitive.Root ref={ref} dir={dir ?? (i18n.dir() as 'ltr' | 'rtl')} {...props} />;
});
Tabs.displayName = 'Tabs';

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      className
    )}
    {...props}
  />
));
TabsList.displayName = 'TabsList';

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };
export default Tabs;
